import { ProctoringOption } from "@verity/proctoring-option/types";
import { CategoryOptionsListProps, OptionProps } from "../proctoringOptionsGrid";
import { VerityApi } from "../../../api/fetch";
import { useState } from "react";
import { produce, type Producer } from "immer";
import OptionBlock from "../optionBlock";
import { getUnsupportedToggleText, PROCTORING_OPTION_TEXT } from "../option-text";
import i18nPackage from "../../../i18n/i18n";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import { CoreButton } from "../../../core";
import FlaggedKeywordsModal from "../../edit-quiz-modals/flaggedKeywordsModal";
import styles from "./index.module.css";

// # In case we need to bring back the options
// function FaceTrackingOption<T extends Partial<ProctoringOption>>({
// 	option,
// 	updateOption,
// 	allowStrictMode,
// 	disabled,
// 	notAllowed,
// 	notAllowedMessage,
// }: OptionProps<T> & { allowStrictMode: boolean }) {
// 	const { videoRecordingMode, faceTrackingMode } = option;
// 	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(faceTrackingMode !== "strict");

// 	return (
// 		<OptionBlock
// 			disabled={
// 				videoRecordingMode === "off" ||
// 				!isOptionSupported(option, "faceTrackingMode", "off") ||
// 				!isOptionSupported(option, "faceTrackingMode", "normal")
// 			}
// 			disabledMessage={
// 				videoRecordingMode === "off"
// 					? `Please enable the "Record Video" options.`
// 					: getUnsupportedToggleText(
// 							option,
// 							"faceTrackingMode",
// 							faceTrackingMode !== "off" ? "off" : "normal",
// 							faceTrackingMode !== "off",
// 						)
// 			}
// 			fullDisabled={disabled || notAllowed}
// 			checked={videoRecordingMode !== "off" && faceTrackingMode !== "off"}
// 			updateState={(checked) =>
// 				updateOption({
// 					...option,
// 					faceTrackingMode: checked ? "normal" : "off",
// 				})
// 			}
// 			name="trackFace"
// 			description={notAllowed ? notAllowedMessage : PROCTORING_OPTION_TEXT.faceTrackingMode.description}
// 			icon={<FaceDetection />}
// 			contentHeader={PROCTORING_OPTION_TEXT.faceTrackingMode.header}
// 			menuItems={
// 				allowStrictMode && isOptionSupported(option, "faceTrackingMode", "strict") ? (
// 					<CheckBoxMenuSubItem
// 						label="Bypass Mode"
// 						description="Students can continue with their assessment if more than one person is in view."
// 						checked={bypassWasChecked}
// 						onChange={(checked) => {
// 							updateOption({
// 								...option,
// 								faceTrackingMode: faceTrackingMode === "off" ? "off" : checked ? "normal" : "strict",
// 							});
// 							setBypassWasChecked(checked);
// 						}}
// 					/>
// 				) : undefined
// 			}
// 		/>
// 	);
// }

// function ObjectDetectionOption<T extends Partial<ProctoringOption>>({
// 	option,
// 	updateOption,
// 	disabled,
// 	notAllowed,
// 	notAllowedMessage,
// }: OptionProps<T>) {
// 	const { videoRecordingMode, objectDetectionMode } = option;
// 	return (
// 		<OptionBlock
// 			disabled={
// 				videoRecordingMode === "off" ||
// 				!isOptionSupported(option, "objectDetectionMode", "off") ||
// 				!isOptionSupported(option, "objectDetectionMode", "normal")
// 			}
// 			disabledMessage={
// 				videoRecordingMode === "off"
// 					? `Please enable the "Record Video" options.`
// 					: getUnsupportedToggleText(
// 							option,
// 							"objectDetectionMode",
// 							objectDetectionMode !== "off" ? "off" : "normal",
// 							objectDetectionMode !== "off",
// 						)
// 			}
// 			fullDisabled={disabled || notAllowed}
// 			checked={videoRecordingMode !== "off" && objectDetectionMode !== "off"}
// 			updateState={(checked) =>
// 				updateOption({
// 					...option,
// 					objectDetectionMode: checked ? "normal" : "off",
// 				})
// 			}
// 			name="suspiciousObjects"
// 			description={notAllowed ? notAllowedMessage : PROCTORING_OPTION_TEXT.objectDetectionMode.description}
// 			icon={<ObjectDetection />}
// 			contentHeader={PROCTORING_OPTION_TEXT.objectDetectionMode.header}
// 		/>
// 	);
// }

function RecordVideoOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { videoRecordingMode, faceTrackingMode, objectDetectionMode } = option;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(videoRecordingMode !== "strict");

	const notAllowed = !isOptionAllowed("videoRecordingMode");
	return (
		<OptionBlock
			name="recordVideo"
			checked={videoRecordingMode !== "off"}
			updateState={(checked) =>
				updateOption({
					...option,
					videoRecordingMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
					faceTrackingMode: checked ? faceTrackingMode : "off",
					objectDetectionMode: checked ? objectDetectionMode : "off",
				})
			}
			description={PROCTORING_OPTION_TEXT.videoRecordingMode.description}
			Icon={PROCTORING_OPTION_TEXT.videoRecordingMode.icon}
			contentHeader={PROCTORING_OPTION_TEXT.videoRecordingMode.header}
			disabled={notAllowed}
			fullDisabled={disabled || notAllowed}
			disabledMessage={notAllowed ? i18nPackage.t("editQuizPage.configurationStep.instituteDisabledVideo") : ""}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("videoRecordingMode")}
			menuItems={
				<div>
					{isOptionAllowed("videoRecordingMode", "normal") && isOptionAllowed("videoRecordingMode", "strict") && (
						<CheckBoxMenuSubItem
							label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
							checked={bypassWasChecked}
							onChange={(checked) => {
								updateOption({
									...option,
									videoRecordingMode: checked ? "normal" : "strict",
								});
								setBypassWasChecked(checked);
							}}
						/>
					)}
					{isOptionAllowed("faceTrackingMode") && (
						<div data-automation="detect_people_container">
							<CheckBoxMenuSubItem
								label={i18nPackage.t(
									"editQuizPage.configurationStep.recordOptionsTSX.videoRecordingMode.faceTrackingMode",
								)}
								description={PROCTORING_OPTION_TEXT.faceTrackingMode.description}
								checked={videoRecordingMode !== "off" && faceTrackingMode !== "off"}
								onChange={(checked) =>
									updateOption({
										...option,
										faceTrackingMode: checked ? "normal" : "off",
									})
								}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("faceTrackingMode")}
							/>
							{isOptionAllowed("faceTrackingMode", "normal") &&
								isOptionAllowed("faceTrackingMode", "strict") &&
								videoRecordingMode !== "off" &&
								faceTrackingMode !== "off" && (
									<div style={{ marginLeft: "25px" }}>
										<CheckBoxMenuSubItem
											label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
											checked={faceTrackingMode === "normal"}
											onChange={(checked) => {
												updateOption({
													...option,
													faceTrackingMode: checked ? "normal" : "strict",
												});
											}}
										/>
									</div>
								)}
						</div>
					)}
					{isOptionAllowed("objectDetectionMode") && (
						<CheckBoxMenuSubItem
							label={i18nPackage.t(
								"editQuizPage.configurationStep.recordOptionsTSX.videoRecordingMode.objectDetectionMode",
							)}
							description={PROCTORING_OPTION_TEXT.objectDetectionMode.description}
							checked={videoRecordingMode !== "off" && objectDetectionMode !== "off"}
							onChange={(checked) =>
								updateOption({
									...option,
									objectDetectionMode: checked ? "normal" : "off",
								})
							}
							partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("objectDetectionMode")}
						/>
					)}
				</div>
			}
			horizontalOrientation={horizontalOrientation}
		/>
	);
}

function RecordAudioOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	instituteUuid,
	getInstituteSettings,
	getPartiallySupportedPlatforms,
}: OptionProps<T> & {
	instituteUuid?: string;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
}) {
	// pure states derived from singular option values
	const [internalOptionState, setInternalOptionState] = useState({
		isAudioRecordingOn: option.audioRecordingMode !== "off",
		isBypassModeOn: option.audioRecordingMode !== "strict",
		isQuietEnvironmentOn: Boolean(option.quietEnvironment),
		isAudioTranscriptionOn: Boolean(option.audioTranscription),
		isFlaggedKeywordsOn: option.flaggedKeywords !== null,
		flaggedKeywordData: option.flaggedKeywords,
	});
	const [showKeywordsModal, setShowKeywordsModal] = useState(false);

	// compute derived state as a sideeffect and update options
	const updateInternalState = (recipe: Producer<typeof internalOptionState>) => {
		setInternalOptionState((prev) => {
			const newInternalOptionState = produce(prev, recipe);
			const {
				isAudioRecordingOn,
				isBypassModeOn,
				isQuietEnvironmentOn,
				isAudioTranscriptionOn,
				isFlaggedKeywordsOn,
				flaggedKeywordData,
			} = newInternalOptionState;

			// open the modal if the internal state is directly toggled on
			if (prev.isFlaggedKeywordsOn === false && isFlaggedKeywordsOn === true) {
				setShowKeywordsModal(true);
			}

			updateOption(
				produce(option, (draft) => {
					draft.audioRecordingMode = isAudioRecordingOn ? (isBypassModeOn ? "normal" : "strict") : "off";
					draft.quietEnvironment = isAudioRecordingOn && isQuietEnvironmentOn;
					draft.audioTranscription = isAudioRecordingOn && isAudioTranscriptionOn;
					draft.flaggedKeywords =
						isAudioRecordingOn && isAudioTranscriptionOn && isFlaggedKeywordsOn ? flaggedKeywordData : null;
				}),
			);
			return newInternalOptionState;
		});
	};

	const notAllowed = !isOptionAllowed("audioRecordingMode");
	return (
		<OptionBlock
			checked={internalOptionState.isAudioRecordingOn}
			updateState={(checked) =>
				updateInternalState((draft) => {
					draft.isAudioRecordingOn = checked;
				})
			}
			name="recordAudio"
			description={PROCTORING_OPTION_TEXT.audioRecordingMode.description}
			Icon={PROCTORING_OPTION_TEXT.audioRecordingMode.icon}
			contentHeader={PROCTORING_OPTION_TEXT.audioRecordingMode.header}
			disabled={notAllowed}
			fullDisabled={disabled || notAllowed}
			disabledMessage={notAllowed ? i18nPackage.t("editQuizPage.configurationStep.instituteDisabledAudio") : ""}
			menuItems={
				<div>
					{isOptionAllowed("audioRecordingMode", "normal") && isOptionAllowed("audioRecordingMode", "strict") && (
						<CheckBoxMenuSubItem
							label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
							checked={internalOptionState.isBypassModeOn}
							onChange={(checked) =>
								updateInternalState((draft) => {
									draft.isBypassModeOn = checked;
								})
							}
							partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("audioRecordingMode", "strict")}
						/>
					)}
					<CheckBoxMenuSubItem
						label={PROCTORING_OPTION_TEXT.quietEnvironment.header}
						description={PROCTORING_OPTION_TEXT.quietEnvironment.description}
						checked={internalOptionState.isQuietEnvironmentOn}
						onChange={(checked) =>
							updateInternalState((draft) => {
								draft.isQuietEnvironmentOn = checked;
							})
						}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("quietEnvironment")}
					/>
					{isOptionAllowed("audioTranscription") && (
						<>
							<CheckBoxMenuSubItem
								label={PROCTORING_OPTION_TEXT.audioTranscription.header}
								checked={internalOptionState.isAudioTranscriptionOn}
								onChange={(checked) =>
									updateInternalState((draft) => {
										draft.isAudioTranscriptionOn = checked;
									})
								}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("audioTranscription")}
							/>
							{isOptionAllowed("flaggedKeywords") && internalOptionState.isAudioTranscriptionOn && (
								<div style={{ marginLeft: "25px" }}>
									<CheckBoxMenuSubItem
										label={PROCTORING_OPTION_TEXT.flaggedKeywords.header}
										checked={internalOptionState.isFlaggedKeywordsOn}
										onChange={(checked) =>
											updateInternalState((draft) => {
												draft.isFlaggedKeywordsOn = checked;
											})
										}
									/>
									{internalOptionState.isFlaggedKeywordsOn && (
										<div style={{ marginLeft: "32px", marginTop: "8px" }}>
											<CoreButton color="primary" size="small" onClick={() => setShowKeywordsModal(true)}>
												{i18nPackage.t("editQuizPage.configurationStep.recordOptionsTSX.flaggedKeywords")}
											</CoreButton>
										</div>
									)}
								</div>
							)}
							{showKeywordsModal && (
								<FlaggedKeywordsModal
									onClose={() => setShowKeywordsModal(false)}
									instituteUuid={instituteUuid}
									getInstituteSettings={getInstituteSettings}
									optionKeywords={internalOptionState.flaggedKeywordData ?? []}
									onSaveOptionKeywords={(keywords) =>
										updateInternalState((draft) => {
											draft.flaggedKeywordData = keywords;
										})
									}
								/>
							)}
						</>
					)}
				</div>
			}
			horizontalOrientation={horizontalOrientation}
		/>
	);
}

export default function RecordOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	shouldShowOption,
	isOptionAllowed,
	horizontalOrientation,
	instituteUuid,
	getInstituteSettings,
	getPartiallySupportedPlatforms,
}: CategoryOptionsListProps<T> & {
	instituteUuid?: string;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
}) {
	const { recordScreen, scanRoom } = option;

	const recordScreenAllowed = isOptionAllowed("recordScreen");
	const scanRoomAllowed = isOptionAllowed("scanRoom");
	return (
		<div className={styles.proctorSettingsGrid}>
			{shouldShowOption("videoRecordingMode") && (
				<RecordVideoOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("audioRecordingMode") && (
				<RecordAudioOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					instituteUuid={instituteUuid}
					getInstituteSettings={getInstituteSettings}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}

			{shouldShowOption("recordScreen") && (
				<OptionBlock
					name="recordScreen"
					checked={Boolean(recordScreen)}
					updateState={(checked) =>
						updateOption({
							...option,
							recordScreen: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.recordScreen.description}
					Icon={PROCTORING_OPTION_TEXT.recordScreen.icon}
					contentHeader={PROCTORING_OPTION_TEXT.recordScreen.header}
					disabled={!recordScreenAllowed}
					fullDisabled={disabled || !recordScreenAllowed}
					disabledMessage={
						!recordScreenAllowed
							? i18nPackage.t("editQuizPage.configurationStep.instituteDisabledScreen")
							: getUnsupportedToggleText(option, "recordScreen", !recordScreen, Boolean(recordScreen))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("recordScreen")}
				/>
			)}

			{shouldShowOption("scanRoom") && (
				<OptionBlock
					name="scanRoom"
					checked={Boolean(scanRoom)}
					updateState={(checked) =>
						updateOption({
							...option,
							scanRoom: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.scanRoom.description}
					Icon={PROCTORING_OPTION_TEXT.scanRoom.icon}
					contentHeader={PROCTORING_OPTION_TEXT.scanRoom.header}
					disabled={!scanRoomAllowed}
					fullDisabled={!isOptionAllowed("scanRoom") || disabled}
					disabledMessage={
						!scanRoomAllowed
							? i18nPackage.t("editQuizPage.configurationStep.instituteDisabledRoom")
							: getUnsupportedToggleText(option, "scanRoom", !scanRoom, Boolean(scanRoom))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("scanRoom")}
				/>
			)}
		</div>
	);
}
